import { useEffect, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  api,
  type AssessmentRow,
  type AssessmentRunAssignableTeam,
  type DeleteResultsBody,
  type DeleteResultsPreview,
} from "../../lib/api";
import { useConfirmDialog } from "../../components/ConfirmDialog.helpers";

import { CollapsibleSection } from "./shared";

// local time. Used by DeleteResultsCard to seed inputs from the
// page-level range without manual typing.
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function DeleteResultsCard({
  defaultRange,
}: {
  defaultRange: { start: Date; end: Date } | null;
}) {
  const qc = useQueryClient();
  const dialog = useConfirmDialog();
  const [fromStr, setFromStr] = useState<string>("");
  const [toStr, setToStr] = useState<string>("");
  const [teamId, setTeamId] = useState<string>("");
  const [assessmentId, setAssessmentId] = useState<string>("");
  // Seed inputs from the page-level range until the operator types.
  // Once they touch either field we stop syncing so editing is sticky.
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<
    { deleted_count: number; deleted_jobs: number } | null
  >(null);

  useEffect(() => {
    if (touched || !defaultRange) return;
    setFromStr(toLocalInput(defaultRange.start));
    setToStr(toLocalInput(defaultRange.end));
  }, [defaultRange, touched]);

  const teamsQ = useQuery<AssessmentRunAssignableTeam[]>({
    // Reuse the same key as Phase3AssessmentsCard so we don't double-fetch.
    queryKey: ["assessment-runs-assignable-teams"],
    queryFn: () => api.assessmentRunsAssignableTeams(),
    placeholderData: keepPreviousData,
  });

  const assessmentsQ = useQuery<AssessmentRow[]>({
    queryKey: ["assessments-list"],
    queryFn: () => api.listAssessments(),
    placeholderData: keepPreviousData,
  });

  function buildBody(): DeleteResultsBody | null {
    if (!fromStr || !toStr) {
      setError("Pick a start and end");
      return null;
    }
    const fromDate = new Date(fromStr);
    const toDate = new Date(toStr);
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      setError("Invalid date/time");
      return null;
    }
    if (fromDate >= toDate) {
      setError("`from` must be earlier than `to`");
      return null;
    }
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    if (toDate.getTime() - fromDate.getTime() > THIRTY_DAYS_MS) {
      setError("Window cannot exceed 30 days");
      return null;
    }
    return {
      from: fromDate.toISOString(),
      to: toDate.toISOString(),
      team_id: teamId || null,
      assessment_id: assessmentId || null,
    };
  }

  const deleteM = useMutation({
    mutationFn: (body: DeleteResultsBody) =>
      api.deleteResults(body),
    onSuccess: (data) => {
      setLastResult({
        deleted_count: data.deleted_count,
        deleted_jobs: data.deleted_jobs,
      });
      // Live run status feeds off these — invalidate so the operator
      // sees the cleared state immediately in adjacent cards.
      qc.invalidateQueries({ queryKey: ["assessment-run-status"] });
      qc.invalidateQueries({ queryKey: ["assessment-run-active"] });
      qc.invalidateQueries({ queryKey: ["assessment-run-latest"] });
    },
    onError: (e: { message?: string }) => {
      setError(e.message || "Delete failed");
    },
  });

  const previewM = useMutation({
    mutationFn: (body: DeleteResultsBody) =>
      api.previewDeleteResults(body),
  });

  async function onConfirmDelete() {
    setError(null);
    setLastResult(null);
    const body = buildBody();
    if (!body) return;
    let preview: DeleteResultsPreview;
    try {
      preview = await previewM.mutateAsync(body);
    } catch (e) {
      setError((e as { message?: string }).message || "Preview failed");
      return;
    }
    const teamLabel = body.team_id
      ? teamsQ.data?.find((t) => t.id === body.team_id)?.name ?? body.team_id
      : "all teams";
    const assessmentLabel = body.assessment_id
      ? assessmentsQ.data?.find((a) => a.id === body.assessment_id)?.name ??
        body.assessment_id
      : "all assessments";
    const lines = [
      `Delete ${preview.matched_count} assessment result(s)?`,
      ``,
      `Window: ${body.from} → ${body.to}`,
      `Team: ${teamLabel}`,
      `Assessment: ${assessmentLabel}`,
    ];
    if (preview.by_assessment.length) {
      lines.push("", "By assessment:");
      for (const g of preview.by_assessment) {
        lines.push(`  • ${g.name}: ${g.count}`);
      }
    }
    if (preview.by_team.length) {
      lines.push("", "By team:");
      for (const g of preview.by_team) {
        lines.push(`  • ${g.name}: ${g.count}`);
      }
    }
    lines.push(
      "",
      "This will also remove detection rows and orphaned assessment_run jobs.",
      "This cannot be undone.",
    );
    const ok = await dialog.confirm({
      title: `Delete ${preview.matched_count} assessment result(s)?`,
      message: lines.slice(2).join("\n"),
      confirmLabel: "Delete results",
      destructive: true,
    });
    if (!ok) return;
    deleteM.mutate(body);
  }

  const submitting = previewM.isPending || deleteM.isPending;

  return (
    <CollapsibleSection
      title="Delete Assessment Results"
      ariaLabel="Delete Results"
      subtitle="Dev tool — bulk-delete assessment results within a window (max 30 days). Detections cascade; orphan assessment_run jobs are cleaned up."
      borderClassName="border border-destructive/40"
      autoExpand={!!error}
    >
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">From</span>
            <input
              type="datetime-local"
              className="rounded border border-border bg-background px-2 py-1"
              value={fromStr}
              onChange={(e) => {
                setTouched(true);
                setFromStr(e.target.value);
              }}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">To</span>
            <input
              type="datetime-local"
              className="rounded border border-border bg-background px-2 py-1"
              value={toStr}
              onChange={(e) => {
                setTouched(true);
                setToStr(e.target.value);
              }}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Team (optional)</span>
            <select
              className="rounded border border-border bg-background px-2 py-1"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
            >
              <option value="">All teams</option>
              {(teamsQ.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Assessment (optional)</span>
            <select
              className="rounded border border-border bg-background px-2 py-1"
              value={assessmentId}
              onChange={(e) => setAssessmentId(e.target.value)}
            >
              <option value="">All assessments</option>
              {(assessmentsQ.data ?? [])
                // Saved-only: drafts have never produced results so they'd
                // only clutter the picker.
                .filter((a) => a.status === "saved")
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
          </label>
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        {lastResult && (
          <p className="text-sm text-foreground">
            Deleted {lastResult.deleted_count} result(s) and
            {" "}
            {lastResult.deleted_jobs} orphan job(s).
          </p>
        )}
        <div>
          <button
            type="button"
            onClick={onConfirmDelete}
            disabled={submitting}
            className="rounded bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground disabled:opacity-50"
          >
            {submitting ? "Working…" : "Delete results"}
          </button>
        </div>
      </div>
      {dialog.element}
    </CollapsibleSection>
  );
}
